import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete } from '../../api/client';
import ToolPageVisualEditor from './toolPageEditor/ToolPageVisualEditor';
import { emptyPage } from './toolPageEditor/shared';

export default function AdminToolPages() {
  const [pages, setPages] = useState([]);
  const [form, setForm] = useState(emptyPage());
  const [editingSlug, setEditingSlug] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState('');

  const load = () => apiGet('/admin/tool-pages').then(setPages);

  useEffect(() => {
    load().catch(console.error);
  }, []);

  const stats = {
    total: pages.length,
    published: pages.filter((p) => p.published).length,
    draft: pages.filter((p) => !p.published).length,
  };

  const startNew = () => {
    setEditingSlug(null);
    setForm(emptyPage());
    setMessage('');
    setEditorOpen(true);
  };

  const startEdit = (page) => {
    setEditingSlug(page.slug);
    setForm({
      slug: page.slug,
      title: page.title,
      seo: { ...page.seo },
      hero: { ...page.hero },
      content: JSON.parse(JSON.stringify(page.content || { sections: [] })),
      published: page.published,
      sortOrder: page.sortOrder || 0,
    });
    setMessage('');
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingSlug(null);
    setForm(emptyPage());
    setShowJson(false);
    setMessage('');
  };

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setMessage('');
    try {
      if (editingSlug) {
        await apiPut(`/admin/tool-pages/${editingSlug}`, form);
        setMessage('Tool page saved.');
      } else {
        await apiPost('/admin/tool-pages', form);
        setMessage('Tool page created.');
        setEditingSlug(form.slug);
      }
      load();
    } catch (err) {
      setMessage(err.message);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete tool page "${slug}"?`)) return;
    await apiDelete(`/admin/tool-pages/${slug}`);
    if (editingSlug === slug) closeEditor();
    load();
  };

  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setForm(parsed);
      setShowJson(false);
      setMessage('JSON applied to form.');
    } catch (err) {
      setMessage('Invalid JSON: ' + err.message);
    }
  };

  if (editorOpen) {
    return (
      <>
        <ToolPageVisualEditor
          form={form}
          setForm={setForm}
          editingSlug={editingSlug}
          message={message}
          onSave={handleSave}
          onClose={closeEditor}
          onShowJson={() => { setJsonText(JSON.stringify(form, null, 2)); setShowJson(true); }}
        />
        {showJson && (
          <div className="tpb-json-overlay">
            <div className="admin-json-panel">
              <div className="admin-json-panel-head">
                <h3>JSON editor</h3>
                <button type="button" className="btn btn-secondary" onClick={() => setShowJson(false)}>Close</button>
              </div>
              <textarea className="admin-json" value={jsonText} onChange={(e) => setJsonText(e.target.value)} rows={16} />
              <div className="admin-catalog-form-actions">
                <button type="button" className="btn btn-primary" onClick={applyJson}>Apply JSON</button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="admin-panel-page">
      <div className="admin-panel-toolbar">
        <div className="admin-panel-toolbar-row">
          <div className="admin-stat-chips">
            <div className="admin-stat-chip">
              <span className="admin-stat-value">{stats.total}</span>
              <span className="admin-stat-label">Pages</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--green">
              <span className="admin-stat-value">{stats.published}</span>
              <span className="admin-stat-label">Published</span>
            </div>
            <div className="admin-stat-chip">
              <span className="admin-stat-value">{stats.draft}</span>
              <span className="admin-stat-label">Draft</span>
            </div>
          </div>
          <div className="admin-panel-toolbar-actions">
            <button type="button" className="btn btn-primary" onClick={startNew}>
              <i className="fas fa-plus" aria-hidden="true" />
              New page
            </button>
          </div>
        </div>
      </div>

      <div className="admin-list-wrap">
        {pages.length === 0 ? (
          <div className="admin-list-empty">
            <p>No tool pages yet.</p>
            <button type="button" className="btn btn-primary" onClick={startNew}>Create first page</button>
          </div>
        ) : (
          <ul className="admin-list">
            {pages.map((p) => (
              <li key={p.slug} className="admin-list-item">
                {p.hero?.image ? (
                  <img src={p.hero.image} alt="" className="admin-thumb" />
                ) : (
                  <span className="admin-thumb is-empty"><i className="fas fa-file-alt" aria-hidden="true" /></span>
                )}
                <div className="admin-list-main">
                  <div className="admin-list-title-row">
                    <span className="admin-list-title">{p.title}</span>
                    <span className="admin-list-sub">/tools/{p.slug}</span>
                  </div>
                  <div className="admin-list-pills">
                    <span className={`admin-pill ${p.published ? 'is-green' : 'is-muted'}`}>
                      {p.published ? 'Published' : 'Draft'}
                    </span>
                    <span className="admin-pill is-muted">{p.content?.sections?.length || 0} sections</span>
                  </div>
                </div>
                <div className="admin-list-actions">
                  <Link to={`/tools/${p.slug}`} target="_blank" rel="noopener noreferrer" className="admin-icon-btn" title="View">
                    <i className="fas fa-external-link-alt" aria-hidden="true" />
                  </Link>
                  <button type="button" className="admin-icon-btn" onClick={() => startEdit(p)} title="Edit">
                    <i className="fas fa-pen" aria-hidden="true" />
                  </button>
                  <button type="button" className="admin-icon-btn danger" onClick={() => handleDelete(p.slug)} title="Delete">
                    <i className="fas fa-trash" aria-hidden="true" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
